import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge } from 'react-bootstrap';

import { X, Upload } from 'lucide-react';

const DiamondModal = ({ show, handleClose, diamond, isEdit, onSave }) => {
  const shapes = ['Round', 'Oval', 'Pear', 'Marquise', 'Square (Princess)', 'Emerald', 'Radiant', 'Asscher', 'Cushion', 'Heart'];
  const sources = ['natural', 'lab grown'];
  const cutGrades = ['None', 'Good', 'Very Good', 'Excellent'];
  const clarities = ['SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  const colors = ['K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];

  const [formData, setFormData] = useState({
    color: '',
    fancyColor: '',
    cut: '',
    carat: '',
    source: '',
    clarity: '',
    cut_grade: '',
    symmetry: '',
    polish: '',
    certificate_inst: '',
    certificate_num: '',
    certificate_link: '',
    measurements: '',
    weight: '',
    depth: '',
    lw_ratio: '',
    table_width: '',
    total_depth: '',
    fancy_color_inte: '',
    image_link: '',
    video_link: ''
  });

  useEffect(() => {
    if (diamond) {
      setFormData({
        ...diamond,
        carat: diamond.carat || '',
        price: diamond.price || '',
        weight: diamond.weight || '',
        depth: diamond.depth || '',
        lw_ratio: diamond.lw_ratio || '',
        table_width: diamond.table_width || '',
        total_depth: diamond.total_depth || ''
      });
    } else {
      setFormData({
        color: '',
        fancyColor: '',
        cut: '',
        carat: '',
        price: '',
        source: '',
        clarity: '',
        cut_grade: '',
        symmetry: '',
        polish: '',
        certificate_inst: '',
        certificate_num: '',
        certificate_link: '',
        measurements: '',
        weight: '',
        depth: '',
        lw_ratio: '',
        table_width: '',
        total_depth: '',
        fancy_color_inte: '',
        image_link: '',
        video_link: ''
      });
    }
  }, [diamond, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      carat: formData.carat ? parseFloat(formData.carat) : null,
      price: formData.price ? parseFloat(formData.price) : null,
      weight: formData.weight ? parseFloat(formData.weight) : null,
      depth: formData.depth ? parseFloat(formData.depth) : null,
      table_width: formData.table_width ? parseFloat(formData.table_width) : null,
      total_depth: formData.total_depth ? parseFloat(formData.total_depth) : null,
      cut: formData.cut ? formData.cut.toUpperCase() : formData.cut
    };
    
    if (isEdit && diamond?.diamond_id) {
      payload.diamond_id = diamond.diamond_id;
    }

    onSave(payload);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit Diamond' : 'Add New Diamond'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Shape (Cut)</Form.Label>
                <Form.Select name="cut" value={formData.cut} onChange={handleChange} required>
                  <option value="">Select Shape</option>
                  {shapes.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Source</Form.Label>
                <Form.Select name="source" value={formData.source} onChange={handleChange} required>
                  <option value="">Select Source</option>
                  {sources.map(s => <option key={s} value={s}>{s}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Carat</Form.Label>
                <Form.Control type="number" step="0.01" name="carat" value={formData.carat} onChange={handleChange} required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Price</Form.Label>
                <Form.Control type="number" step="0.01" name="price" value={formData.price} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Color</Form.Label>
                <Form.Select name="color" value={formData.color} onChange={handleChange}>
                  <option value="">Select Color</option>
                  {colors.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Fancy Color</Form.Label>
                <Form.Control name="fancyColor" value={formData.fancyColor} onChange={handleChange} placeholder="e.g. Yellow" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Clarity</Form.Label>
                <Form.Select name="clarity" value={formData.clarity} onChange={handleChange} required>
                  <option value="">Select Clarity</option>
                  {clarities.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group className="mb-3">
                    <Form.Label>Cut Grade</Form.Label>
                    <Form.Select name="cut_grade" value={formData.cut_grade} onChange={handleChange}>
                        <option value="">Select Cut Grade</option>
                        {cutGrades.map(g => <option key={g} value={g}>{g}</option>)}
                    </Form.Select>
                </Form.Group>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Symmetry</Form.Label>
                <Form.Control name="symmetry" value={formData.symmetry} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Polish</Form.Label>
                <Form.Control name="polish" value={formData.polish} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
                <Form.Group className="mb-3">
                    <Form.Label>Certificate Inst</Form.Label>
                    <Form.Control name="certificate_inst" value={formData.certificate_inst} onChange={handleChange} />
                </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Certificate Num</Form.Label>
                <Form.Control name="certificate_num" value={formData.certificate_num} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Certificate Link</Form.Label>
                <Form.Control name="certificate_link" value={formData.certificate_link} onChange={handleChange} placeholder="https://..." />
              </Form.Group>
            </Col>
          </Row>
          <hr />
          <h6>Dimensions & Media</h6>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Measurements</Form.Label>
                <Form.Control name="measurements" value={formData.measurements} onChange={handleChange} placeholder="e.g. 5.10 x 5.12 x 3.15" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Weight</Form.Label>
                <Form.Control type="number" step="0.01" name="weight" value={formData.weight} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Depth</Form.Label>
                <Form.Control type="number" step="0.01" name="depth" value={formData.depth} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>LW Ratio</Form.Label>
                <Form.Control name="lw_ratio" value={formData.lw_ratio} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Table Width</Form.Label>
                <Form.Control type="number" step="0.01" name="table_width" value={formData.table_width} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Total Depth</Form.Label>
                <Form.Control type="number" step="0.01" name="total_depth" value={formData.total_depth} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Fancy Color Intensity</Form.Label>
                <Form.Control name="fancy_color_inte" value={formData.fancy_color_inte} onChange={handleChange} />
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><Upload size={16} className="me-1" /> Image Link</Form.Label>
                <Form.Control name="image_link" value={formData.image_link} onChange={handleChange} placeholder="https://external-image.com/..." />
                {formData.image_link && (
                    <div className="mt-2 text-center">
                        <img src={formData.image_link} alt="Diamond Preview" className="img-thumbnail" style={{ maxHeight: '100px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><Upload size={16} className="me-1" /> Video Link</Form.Label>
                <Form.Control name="video_link" value={formData.video_link} onChange={handleChange} placeholder="https://external-video.com/..." />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" type="submit" style={{ backgroundColor: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}>
            {isEdit ? 'Update Diamond' : 'Add Diamond'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DiamondModal;
